/**
 * MesEglisesSection — gère la liste des églises du réseau du pasteur.
 * Création limitée selon le plan, soft delete protégeant la dernière église.
 */
import { useState, useEffect, useCallback } from "react";
import { colors, fonts } from "../styles/tokens";
import { eglisesApi } from "../services/api";
import useAuth from "../hooks/useAuth";
import useToast from "../hooks/useToast";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Modal from "../components/Modal";
import Input from "../components/Input";
import Select from "../components/Select";

const PAYS = [
  "Côte d'Ivoire", "Sénégal", "Burkina Faso", "Mali", "Bénin", "Togo",
  "Cameroun", "Gabon", "Tchad", "Congo (RDC)", "Congo (Brazzaville)",
  "France", "Belgique", "Suisse", "Canada", "Haïti", "Madagascar", "Maurice", "Autre",
];
const DEVISES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "XOF", label: "XOF (F CFA Ouest)" },
  { value: "XAF", label: "XAF (F CFA Centre)" },
  { value: "CAD", label: "CAD ($)" },
  { value: "CHF", label: "CHF" },
  { value: "USD", label: "USD ($)" },
];

// Limites alignées avec PLAN_LIMITS du backend
const LIMITES_PAR_PLAN = {
  freemium: 1,
  communaute: 1,
  starter: 3,
  pro: 999,
  enterprise: 999,
};

const PLAN_LABELS = {
  freemium: "Freemium",
  communaute: "Communauté",
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
};

const EMPTY = { nom: "", ville: "", pays: "Côte d'Ivoire", devise: "XOF", pasteur_principal: "" };

export default function MesEglisesSection() {
  const { user } = useAuth();
  const toast = useToast();

  const [eglises, setEglises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planActuel, setPlanActuel] = useState("freemium");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await eglisesApi.mesEglises();
      setEglises(data || []);
      // Note : le plan vient idéalement d'un endpoint dédié /reseau/mon-plan ;
      // ici on infère depuis la première église (toutes du même réseau)
      // Pour la démo on lit le plan via une heuristique simple.
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const limit = LIMITES_PAR_PLAN[planActuel] || 1;
  const limitAtteinte = eglises.length >= limit;

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (eglise) => { setEditing(eglise); setFormOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await eglisesApi.update(editing.id, payload);
        toast.success("Église mise à jour");
      } else {
        await eglisesApi.create(payload);
        toast.success(`Église « ${payload.nom} » créée`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleDelete = async (eglise) => {
    if (eglises.length <= 1) {
      toast.error("Impossible de supprimer la seule église du réseau");
      return;
    }
    if (!window.confirm(`Désactiver l'église « ${eglise.nom} » ?`)) return;
    try {
      await eglisesApi.remove(eglise.id);
      toast.success("Église désactivée");
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return <div style={{ color: colors.textMuted, fontSize: "13px" }}>Chargement...</div>;
  }

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
        <div>
          <h2 style={{ fontFamily: fonts.heading, fontSize: "22px", marginBottom: "4px" }}>
            Mes Églises ({eglises.length})
          </h2>
          <div style={{ color: colors.textMuted, fontSize: "13px" }}>
            Plan {PLAN_LABELS[planActuel] || planActuel} ·{" "}
            <span style={{ color: limitAtteinte ? colors.rose : colors.gold, fontWeight: 600 }}>
              {eglises.length}/{limit === 999 ? "∞" : limit}
            </span>{" "}
            église{eglises.length > 1 ? "s" : ""}
          </div>
        </div>
        <Button
          onClick={openCreate}
          disabled={limitAtteinte}
          title={limitAtteinte ? `Limite atteinte (${eglises.length}/${limit})` : ""}
        >
          + Ajouter une église
        </Button>
      </div>

      {/* Liste des églises */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {eglises.map((e) => (
          <EgliseRow
            key={e.id}
            eglise={e}
            isDefault={e.id === user?.eglise_id}
            onEdit={() => openEdit(e)}
            onDelete={eglises.length > 1 ? () => handleDelete(e) : null}
            onCodeUpdated={(updated) => setEglises((list) => list.map((x) => x.id === updated.id ? { ...x, ...updated } : x))}
          />
        ))}
      </div>

      {/* Banner upsell si limite atteinte */}
      {limitAtteinte && limit < 999 && (
        <div style={{
          marginTop: "16px",
          padding: "12px 16px",
          background: colors.goldDim,
          border: `1px solid ${colors.goldBorder}`,
          borderRadius: "10px",
          fontSize: "13px",
          color: colors.text,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
        }}>
          <span style={{ fontSize: "18px" }}>💡</span>
          <span style={{ flex: 1 }}>
            <strong style={{ color: colors.gold }}>
              Plan {limit === 1 ? "Starter" : "Pro"} pour {limit === 1 ? "3" : "des"} églises
            </strong>{" "}
            — passez à un plan supérieur pour gérer plusieurs assemblées.
          </span>
          <Button size="sm" variant="ghost">Voir les plans →</Button>
        </div>
      )}

      <EgliseForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={handleSubmit}
        onDelete={editing && eglises.length > 1 ? handleDelete : null}
        initial={editing}
      />
    </Card>
  );
}

// ─── Ligne d'église avec code d'invitation ───
function EgliseRow({ eglise, isDefault, onEdit, onDelete, onCodeUpdated }) {
  const toast = useToast();
  const [regenerating, setRegenerating] = useState(false);

  const code = eglise.invitation_code;
  const inscriptionUrl = "https://mon-ministere.com/inscription";

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const regenerer = async () => {
    if (code && !window.confirm(
      "Régénérer le code ?\n\nL'ancien code ne fonctionnera plus. Pensez à transmettre le nouveau à vos fidèles."
    )) return;
    setRegenerating(true);
    try {
      const { data } = await eglisesApi.regenererCode(eglise.id);
      onCodeUpdated?.(data);
      toast.success(code ? "Nouveau code généré" : "Code généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setRegenerating(false);
    }
  };

  const shareWa = code
    ? `https://wa.me/?text=${encodeURIComponent(
        `Bonjour 🙏 Rejoignez notre église ${eglise.nom} sur Mon Ministère avec le code : ${code}\n👉 ${inscriptionUrl}`
      )}`
    : null;

  return (
    <div
      style={{
        padding: "14px 16px",
        background: colors.bg,
        borderRadius: "10px",
        border: `1px solid ${colors.border}`,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {/* Ligne d'identification */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "22px" }}>⛪</span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <strong style={{ fontSize: "15px", color: colors.text }}>{eglise.nom}</strong>
            {isDefault && <Badge variant="gold" size="sm">Par défaut</Badge>}
          </div>
          <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
            {eglise.ville} · {eglise.pays} · {eglise.devise} · {eglise.nb_membres ?? 0} membre{(eglise.nb_membres ?? 0) > 1 ? "s" : ""}
          </div>
          {eglise.pasteur_principal && (
            <div style={{ color: colors.textMuted, fontSize: "12px", marginTop: "2px" }}>
              Pasteur : {eglise.pasteur_principal}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          <Button size="sm" variant="ghost" onClick={onEdit}>Modifier</Button>
          {onDelete && <Button size="sm" variant="danger" onClick={onDelete}>🗑</Button>}
        </div>
      </div>

      {/* Bloc code d'invitation */}
      <div style={{
        background: colors.card,
        border: `1px solid ${colors.goldBorder}`,
        borderRadius: 10,
        padding: "12px 14px",
      }}>
        <div style={{ fontSize: 12, color: colors.gold, fontWeight: 700, letterSpacing: "0.05em", marginBottom: 8 }}>
          🔑 CODE D'INVITATION FIDÈLES
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <code style={{
            fontFamily: "monospace",
            fontSize: 18,
            fontWeight: 700,
            color: code ? colors.text : colors.textMuted,
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            padding: "6px 14px",
            borderRadius: 8,
            letterSpacing: "0.1em",
            minWidth: 100,
            textAlign: "center",
          }}>
            {code || "—"}
          </code>
          {code && (
            <Button size="sm" variant="ghost" onClick={copyCode}>📋 Copier</Button>
          )}
          <Button size="sm" variant="ghost" onClick={regenerer} disabled={regenerating}>
            {regenerating ? "…" : code ? "🔄 Régénérer" : "✨ Générer"}
          </Button>
          {shareWa && (
            <a href={shareWa} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
              <Button size="sm" variant="ghost">📤 Partager sur WhatsApp</Button>
            </a>
          )}
        </div>
        <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 8, lineHeight: 1.5 }}>
          {code
            ? "Partagez ce code à vos fidèles pour qu'ils rejoignent votre église lors de leur inscription."
            : "Cette église n'a pas encore de code. Cliquez sur « Générer » pour en créer un."}
        </p>
      </div>
    </div>
  );
}

// ─── Modale d'édition / création ───
function EgliseForm({ open, onClose, onSubmit, onDelete, initial }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        nom: initial.nom || "",
        ville: initial.ville || "",
        pays: initial.pays || "Côte d'Ivoire",
        devise: initial.devise || "EUR",
        pasteur_principal: initial.pasteur_principal || "",
      });
    } else {
      setForm({ ...EMPTY });
    }
    setErrors({});
    setGlobalError(null);
  }, [open, initial]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => ({ ...p, [key]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.nom.trim() || form.nom.trim().length < 2) e.nom = "Nom requis";
    if (!form.ville.trim()) e.ville = "Ville requise";
    if (!form.pays) e.pays = "Pays requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setGlobalError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await onSubmit({
        nom: form.nom.trim(),
        ville: form.ville.trim(),
        pays: form.pays,
        devise: form.devise,
        pasteur_principal: form.pasteur_principal.trim() || null,
      });
      if (result?.ok === false) setGlobalError(result.error || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title={initial ? "Modifier l'église" : "Nouvelle église"}
      subtitle={initial ? `« ${initial.nom} »` : "Créez une nouvelle église dans votre réseau"}
      size="md"
      footer={
        <>
          {initial && onDelete && (
            <Button
              variant="danger"
              onClick={() => onDelete(initial)}
              disabled={submitting}
              style={{ marginRight: "auto" }}
            >
              🗑 Désactiver
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting}>
            {initial ? "Sauvegarder" : "Créer l'église"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {globalError && (
          <div style={{
            background: "rgba(224,91,106,0.1)",
            border: `1px solid ${colors.rose}`,
            color: colors.rose,
            padding: "10px 14px",
            borderRadius: "10px",
            fontSize: "13px",
          }}>{globalError}</div>
        )}

        <Input
          label="Nom de l'église"
          value={form.nom}
          onChange={set("nom")}
          placeholder="Ex : Église de la Grâce"
          required
          error={errors.nom}
          icon="⛪"
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Input label="Ville" value={form.ville} onChange={set("ville")} required error={errors.ville} />
          <Select
            label="Pays"
            value={form.pays}
            onChange={set("pays")}
            options={PAYS.map((p) => ({ value: p, label: p }))}
            required
            placeholder={null}
          />
        </div>

        <Select label="Devise" value={form.devise} onChange={set("devise")} options={DEVISES} placeholder={null} />

        <Input
          label="Pasteur principal (optionnel)"
          value={form.pasteur_principal}
          onChange={set("pasteur_principal")}
          placeholder="Nom du pasteur responsable"
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
